import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import StatusBadge from "../status-badge";
import { TableButtonContainer, TableContainer } from "../../style";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../InformaAcademy/components/CoursesTable/styles";
import { BiChevronUp } from "react-icons/bi";
import Button from "../../../InformaAcademy/components/button";
import { FiEye } from "react-icons/fi";
import Empty from "../../../InformaAcademy/components/Empty";
import { SortableRow, paginationOptions as options } from "../table-utils";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import moment from "moment";
import { useSelector } from "react-redux";
import { useLoading } from "../../../../hooks/loading";
import api from "../../../../service";
import { useToast } from "../../../../hooks/toast";

export default function ArticleTable({
  articles,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  setSortBy,
  setSortDirection,
  setArticles,
}) {
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);
  const { isLoading, handleLoading } = useLoading();
  const { addToast } = useToast();

  const paginationOptions = {
    ...options({
      locale: intl.locale,
    }),
    disableNext: currentPage * rowsPerPage >= totalRows,
  };

  const reorderRows = useCallback(
    (index, direction, articleId) => {
      handleLoading(true);
      const targetIndex = direction === "up" ? index - 1 : index + 1;

      api
        .put(`/articles/${articleId}/order`,
        {
          order: targetIndex + 1,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((result) => {
          addToast({
            type: "success",
            title: intl.formatMessage({
              id: "success",
            }),
            description: intl.formatMessage({
              id: "success.article_order_updated",
            }),
          });
          setArticles((prevData) => {
            const newData = [...prevData];

            [newData[index], newData[targetIndex]] = [
              newData[targetIndex],
              newData[index],
            ];

            const data = newData.map((item, idx) => ({
              ...item,
              order: idx + 1,
            }));

            return data;
          });
        })
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err?.response?.data?.code,
            }),
          })
        )
        .finally(() => handleLoading(false));
    },
    [addToast, intl, token, handleLoading, setArticles]
  );

  const columns = useMemo(() => {
    return [
      {
        maxWidth: "130px",
        name: <FormattedMessage id={"lesson_order"} />,
        selector: (row) => row.order,
        cell: (row, index) => (
          <SortableRow
            index={index}
            row={row}
            reorderRows={reorderRows}
            rowData={articles}
          />
        ),
      },
      {
        id: "title",
        name: <FormattedMessage id={"article"} />,
        selector: (row) => row.articleTitle,
      },
      {
        maxWidth: "321px",
        name: <FormattedMessage id={"manual_chapter"} />,
        selector: (row) => row.chapterName,
      },
      {
        maxWidth: "172px",
        name: <FormattedMessage id={"updatedAt"} />,
        selector: (row) => moment(row.updatedAt).format("DD/MM/YYYY"),
      },
      {
        maxWidth: "163px",
        name: <FormattedMessage id={"status"} />,
        selector: (row) => <StatusBadge label={row.status} />,
      },
      {
        maxWidth: "215px",
        center: true,
        selector: (row) => <ActionRow id={row.id} status={row.status} />,
      },
    ];
  }, [reorderRows, articles]);

  return (
    <TableContainer>
      <DataTable
        data={articles}
        responsive
        columns={columns}
        noDataComponent={<Empty intlId="articles_empty" />}
        customStyles={customStyles}
        sortIcon={<BiChevronUp style={{ display: "flex" }} />}
        paginationComponentOptions={paginationOptions}
        persistTableHead
        pagination
        paginationServer
        paginationPerPage={rowsPerPage}
        paginationDefaultPage={currentPage}
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(size) => {
          const newPage =
            Math.floor(((currentPage - 1) * rowsPerPage) / size) + 1;
          setCurrentPage(newPage);
          setRowsPerPage(size);
        }}
        paginationTotalRows={totalRows}
        onSort={(column, sortDirection) => {
          setSortBy(column.id);
          setSortDirection(sortDirection);
        }}
      />
    </TableContainer>
  );
}

function ActionRow({ id }) {
  const { manualId } = useParams();
  return (
    <TableButtonContainer center>
      <Button
        label={"visualize"}
        variant={"secondary"}
        size={"sm2"}
        Icon={<FiEye size={13.33} />}
        href={`/admin/manual/${manualId}/details/${id}`}
      />
    </TableButtonContainer>
  );
}
